import { is, List } from "immutable";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import * as redux from "redux";
import { JobAlertsForm } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import { CareerCenterStatuses } from "../../../domain";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";
import { ResumeDetailsPartial } from "../../careerCenter";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    dataLoading: state.careerCenter.loadingResumes,
    resumes: state.careerCenter.userResumes,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    goto: (url: string) => dispatch(push(url)),
    loadData: () => dispatch(Actions.CareerCenter.loadUserResumes()),
    notifyError: (message: string, icon?: string) => dispatch(Actions.Noti.notifyError(message, icon)),
    notifySuccess: (message: string, icon?: string) => dispatch(Actions.Noti.notifySuccess(message, icon)),
    removeResume: (resume: M.CareerCenter.Resume) => dispatch(Actions.CareerCenter.removeFromUserResumes(resume)),
    toggleLoading: (loading: boolean) => dispatch(Actions.CareerCenter.toggleResumesLoading(loading)),
});

type OwnProps = RouteComponentProps<{
    guid: string;
}> & {
};

interface ConnectedState {
    api: Api;
    dataLoading: boolean;
    resumes: List<M.CareerCenter.Resume>;
}

interface ConnectedDispatch {
    goto: (url: string) => void;
    loadData: () => void;
    notifyError: (message: string, icon?: string) => void;
    notifySuccess: (message: string, icon?: string) => void;
    removeResume: (resume: M.CareerCenter.Resume) => void;
    toggleLoading: (loading: boolean) => void;
}

interface OwnState {
    deleted: boolean;
    resume?: M.CareerCenter.Resume;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class ResumeDetailsComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private _confirmRenew: Comp.Modal.ConfirmModal | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
            deleted: false,
            loading: false,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        await this.props.loadData();
    }

    public componentWillMount() {
        if (!this.props.match.params.guid) {
            this.props.notifyError("Missing record identifier");
            this.props.goto(Paths.User.CareerCenter.Resumes);
        }

        // get resume from state if available
        if (this.props.resumes.size)
            this.tryGetResumeFromList(this.props.resumes);
        else
            this.props.toggleLoading(true);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public componentWillUpdate(nextProps: Readonly<CombinedProps>) {
        if (!is(this.props.resumes, nextProps.resumes)) {
            const resume = this.tryGetResumeFromList(nextProps.resumes);

            if (!resume && !this.state.deleted) {
                this.props.notifyError(`Resume record for identifier ${this.props.match.params.guid} could not be found.`);
                this.props.goto(Paths.User.CareerCenter.Resumes);
            }
        }
    }

    public render() {
        const title = this.props.resumes.size === 1
            ? "Your Resume"
            : <Link to={Paths.User.CareerCenter.Resumes}>Your Resumes</Link>;

        const details = this.state.resume
            ? <Details
                api={this.props.api}
                resume={this.state.resume}
                showReturn={this.props.resumes.size > 1}
                onCopy={() => this.handleOnCopy(this.state.resume)}
                onDelete={() => this.toggleDeleteConfirmation(this.state.resume)}
                onEdit={() => this.handleOnEdit(this.state.resume)}
                onRenew={() => this.toggleRenewConfirmation(this.state.resume)}
                onReturn={() => this.props.goto(Paths.User.CareerCenter.Resumes)}
            />
            : null;

        return <Comp.Page.PublicPage style={{ position: "relative", minHeight: "200px" }} pageTitle={this.state.resume ? this.state.resume.title : "Resume details"}>
                <Comp.Generic.LoadingSpinner show={this.state.loading || this.props.dataLoading} />

                <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onProceed={() => this.onDeleteConfirm()}
                    title="Confirm delete"
                    ref={(ref) => this._confirmDelete = ref}
                >
                    <p>Are you sure you want to delete this resume?</p>
                </Comp.Modal.ConfirmModal>

                <Comp.Modal.ConfirmModal
                    headerClassName="info"
                    labelProceed="Renew"
                    onProceed={() => this.onRenewConfirm()}
                    title="Confirm renewal"
                    ref={(ref) => this._confirmRenew = ref}
                >
                    <p>Please confirm you would like to renew this resume.</p>
                </Comp.Modal.ConfirmModal>

                <div id="content-primary">
                    <h1>
                        <Link to={Paths.User.Account}>Your Account</Link> &raquo; {title}
                    </h1>

                    {details}
                </div>

                <div id="content-secondary">
                    <JobAlertsForm />
                </div>
            </Comp.Page.PublicPage>;
    }

    private handleOnCopy(resume?: M.CareerCenter.Resume) {
        if (resume)
            this.props.goto(Paths.CareerCenter.Resume.Post + "?copy=" + resume.guid);
    }

    private handleOnEdit(resume?: M.CareerCenter.Resume) {
        if (resume && resume.statusId === CareerCenterStatuses.Created.id)
            this.props.goto(Paths.CareerCenter.Resume.Post + "/" + resume.guid);
        else if (resume)
            this.props.goto(Paths.User.CareerCenter.EditResume + "/" + resume.guid);
    }

    private async onDeleteConfirm() {
        if (this._confirmDelete === null)
            return;

        const resume = this._confirmDelete.getRef() as M.CareerCenter.Resume;
        if (resume === undefined) {
            this.props.notifyError("Resume is undefined in the confirmation modal.");
            return;
        }

        const qp = { queryParams: { guid: resume.guid }};
        const result = await this.props.api.delete<boolean>(Api.endpoints.users.careerCenter.resume, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            this.setState({ ...this.state, deleted: true }, () => {
                this.props.goto(Paths.User.CareerCenter.Resumes);
                this.props.notifySuccess(`Resume <strong>${resume.title}</strong> has been successfully deleted.`);
                this.props.removeResume(resume);
            });
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }

        if (this._confirmDelete !== null)
            this._confirmDelete.close();
    }

    private async onRenewConfirm() {
        if (this._confirmRenew === null)
            return;

        const resume = this._confirmRenew.getRef() as M.CareerCenter.Resume;
        if (resume === undefined) {
            this.props.notifyError("Resume is undefined in the confirmation modal.");
            return;
        }

        const qp = { queryParams: { guid: resume.guid }};
        const result = await this.props.api.post<M.CareerCenter.Resume>(Api.endpoints.users.careerCenter.renewResume, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            await this.props.loadData();
            this.props.notifySuccess(`Resume <strong>${resume.title}</strong> has been successfully renewed.`);
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }

        if (this._confirmRenew !== null)
            this._confirmRenew.close();
    }

    private toggleDeleteConfirmation(resume?: M.CareerCenter.Resume) {
        if (resume && this._confirmDelete !== null) {
            this._confirmDelete.show(resume);
        }
    }

    private toggleRenewConfirmation(resume?: M.CareerCenter.Resume) {
        if (resume && this._confirmRenew !== null) {
            this._confirmRenew.show(resume);
        }
    }

    private tryGetResumeFromList(list: List<M.CareerCenter.Resume>): M.CareerCenter.Resume | undefined {
        const resume = list.find((x) => x !== undefined && x.guid.toLowerCase() === this.props.match.params.guid.toLowerCase());

        if (resume) {
            this.setState({ ...this.state, resume });
        }

        return resume;
    }
}

export const ResumeDetails: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(ResumeDetailsComponent) as any);

interface DetailsProps {
    api: Api;
    resume: M.CareerCenter.Resume;
    onCopy: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onRenew: () => void;
    onReturn: () => void;
    showReturn: boolean;
}

const Details: React.SFC<DetailsProps> = (props) => {
    const now = new Date();
    const resume = props.resume;
    let expiresOn: Date | undefined;
    let renewBtn = null;

    if (resume.expiresOn) {
        expiresOn = Utility.utcToLocal(resume.expiresOn);
        if (expiresOn && now > expiresOn)
            renewBtn = <BS.Button bsStyle="link" onClick={() => props.onRenew()}><strong>Renew</strong></BS.Button>;
    }

    const returnBtn = props.showReturn
        ? <BS.Button bsStyle="link" onClick={() => props.onReturn()}>&laquo; Return</BS.Button>
        : null;

    const buttons = returnBtn !== null || renewBtn !== null
        ? <BS.ButtonGroup>
            {returnBtn}
            {renewBtn}
        </BS.ButtonGroup>
        : null;

    return <Comp.Generic.WrapperLess>
        <div className="btn-toolbar">
            <BS.ButtonGroup>
                <Link className="btn btn-primary" to={Paths.CareerCenter.Resume.Post}><Comp.Generic.Icon icon="plus" /> Create new</Link>
            </BS.ButtonGroup>

            <Comp.Tools.CrudTools
                entity="resume"
                includeCopy={resume.statusId > CareerCenterStatuses.Created.id}
                onCopy={() => props.onCopy()}
                onDelete={() => props.onDelete()}
                onEdit={() => props.onEdit()}
            >
                {buttons}
            </Comp.Tools.CrudTools>
        </div>

        <ResumeDetailsPartial api={props.api} isLoggedIn resume={props.resume} showExpires />
    </Comp.Generic.WrapperLess>;
};



// WEBPACK FOOTER //
// ./src/containers/user/careerCenter/ResumeDetails.tsx